import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { array, boolean, number, object, ObjectSchema, string } from 'yup'
import { PartiesService } from '../../services/parties-service'
import { ChartBaseFormValues, chartBaseValidationSchema } from './base'
import { CandidateFullName } from './chart-general-form'

export type PartisanChartRacePartiesFormValues = {
  id?: string
  abbv: string
  name: string
  candidates: number
  candidateNames: CandidateFullName[]
}

export type PartisanChartRaceFormValues = {
  id?: string
  name: string
  type: string
  raceType: string
  parties: PartisanChartRacePartiesFormValues[]
  candidates: number
  candidateNames: CandidateFullName[]
  writeIns: number
  voteFor: number
}

export type PartisanChartPartiesFormValues = {
  id?: number
  abbv: string
  name: string
  selected: boolean
}

export type PartisanChartFormValues = ChartBaseFormValues & {
  parties: PartisanChartPartiesFormValues[]
  races: PartisanChartRaceFormValues[]
  createPreferredParty: boolean
}

const chartValidationSchema: ObjectSchema<PartisanChartFormValues> = object({
  ...chartBaseValidationSchema,
  parties: array()
    .of(
      object({
        id: number(),
        abbv: string().required(),
        name: string().required(),
        selected: boolean().required(),
      })
    )
    .required(),
  races: array()
    .of(
      object({
        id: string(),
        name: string().required('Name is required'),
        type: string().required(),
        raceType: string().required(),
        parties: array()
          .of(
            object({
              id: string(),
              abbv: string().required(),
              candidates: number().default(0).required(),
              name: string().required(),
              candidateNames: array()
                .of(
                  object({
                    name: string().required(),
                    order: number().required(),
                  })
                )
                .default([])
                .required(),
            })
          )
          .required()
          .when(['raceType'], {
            is: (type: string) => type === 'Partisan',
            then: (schema) => schema.min(1).required(),
            otherwise: (schema) => schema.optional(),
          }),
        candidates: number()
          .default(0)
          .required()
          .when('type', {
            is: 'Race',
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.optional(),
          }),
        writeIns: number()
          .required()
          .when('type', {
            is: 'Race',
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.optional(),
          }),
        voteFor: number()
          .required()
          .when('type', {
            is: 'Race',
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.optional(),
          }),
        candidateNames: array()
          .of(
            object({
              name: string().required(),
              order: number().required(),
            })
          )
          .default([])
          .required(),
      })
    )
    .min(1, 'Chart must have at least one race')
    .default([])
    .required()
    .test({
      name: 'checkNames',
      message: 'Duplicate race names',
      test: (arr, context) => {
        const namesMap = new Map()

        for (let index = 0; index < arr.length; index++) {
          const race = arr[index]

          if (index === 0) namesMap.set('Non-Partisan', new Map())

          if (race.raceType !== 'Non-Partisan') {
            if (!namesMap.has(race.raceType)) {
              namesMap.set(race.raceType, new Map())
            }

            if (namesMap.get(race.raceType).has(race.name)) {
              return context.createError({
                path: `races[${index}].name`,
                message: 'Duplicate name',
              })
            }

            namesMap.get(race.raceType).set(race.name, index)

            if (
              race.raceType !== 'Non-Partisan' &&
              !namesMap.get('Non-Partisan').has(race.name)
            ) {
              namesMap.get('Non-Partisan').set(race.name, index)
            }
          }
        }

        for (let index = 0; index < arr.length; index++) {
          const race = arr[index]

          if (race.raceType === 'Non-Partisan') {
            if (!namesMap.has(race.raceType)) {
              namesMap.set(race.raceType, new Map())
            }

            if (namesMap.get(race.raceType).has(race.name)) {
              return context.createError({
                path: `races[${index}].name`,
                message: 'Duplicate name',
              })
            }

            namesMap.get(race.raceType).set(race.name, index)
          }
        }

        return true
      },
    }),
  createPreferredParty: boolean().default(false).required(),
}).required()

export function usePartisanChartForm() {
  return useForm<PartisanChartFormValues>({
    resolver: yupResolver(chartValidationSchema),
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues: async () => {
      const parties = await PartiesService.getParties()

      return {
        municipality: '',
        county: '',
        precinct: '',
        electionDate: '',
        showCandidateNames: false,
        createPreferredParty: false,
        parties: parties.map((party) => ({
          id: party.id,
          name: party.name,
          abbv: party.abbv,
          selected: false,
        })),
        races: [],
      }
    },
  })
}
